/*
 * Surgical Suite — Resident Sub-Account Login
 * Standalone route at /surgical-suite/login. Residents authenticate here
 * with the email + password set by their Principal Surgeon. On success
 * the JWT is stashed under `token` (same key as the main app so the
 * shared axios client picks it up) and the user is bounced to the
 * composer at /surgical-suite.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LogIn, ArrowLeft, Stethoscope } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const NAVY = '#07122f';

export default function SurgicalResidentLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/surgical/resident/login', { email: email.trim(), password });
      localStorage.setItem('token', data.token);
      localStorage.setItem('surgical_resident_profile', JSON.stringify({
        profile_id: data.profile_id,
        name: data.name,
        role: data.role,
        principal_doctor_id: data.principal_doctor_id,
        principal_doctor_name: data.principal_doctor_name,
      }));
      toast.success(`Welcome, ${data.name}`);
      nav('/surgical-suite');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: NAVY }}>
            <Stethoscope size={20} className="text-white" />
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[.15em] text-teal-700 uppercase">Surgical Suite</div>
            <h1 className="text-lg font-black" style={{ color: NAVY }}>Resident sign-in</h1>
          </div>
        </div>
        <p className="text-[12px] text-slate-500 mb-5 leading-relaxed">
          Use the email + password set by your Principal Surgeon. The linked voice profile must still pass the voice challenge on the composer before you can dictate.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-teal-500 outline-none mt-1"
                   data-testid="surg-resident-email" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-teal-500 outline-none mt-1"
                   data-testid="surg-resident-password" />
          </div>
          <button type="submit" disabled={busy}
                  className="w-full py-2.5 rounded-lg text-white text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: NAVY }}
                  data-testid="surg-resident-submit">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : <><LogIn size={14} /> Sign in</>}
          </button>
        </form>
        <button type="button" onClick={() => nav('/')} className="mt-4 text-[12px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Back to home
        </button>
      </div>
    </div>
  );
}
